import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isIoTAdminToken, isIotAttachEntryVisible } from "../../utils/iotAttachGate";
import { isContractValid } from "../../utils/isContractValid";
import { getPendingIotConnection } from "../../utils/pendingIotConnectionStorage";
import {
  GridApiPro,
  GridColDef,
  GridValidRowModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import {DocumentNode, useLazyQuery, useQuery} from "@apollo/client";
import _ from "lodash";
import { SearchAssets } from "../../graphql/queries/searchAssets";
import { SearchAssetsDeferred } from "../../graphql/queries/searchAssetsDeferred";
import { getDeferredFieldFlags, hasAnyDeferredFlag } from "../../utils/iotColumnFields";
import {
  formatAssetListSortParam,
  isAssetListColumnSortable,
  parseAssetListSortParam,
  toAssetListSort,
} from "../../utils/assetListSort";
import { hubConnectivityFunc } from "../IoTConnectionWidget/signalStrength";
import SignalStrengthIcon from "../SignalStrengthIcon";
import { Box, Skeleton, Tooltip } from "@mui/material";
import {
  ActionsMenu,
  useUserResources,
} from "@dreebit-registry/vsm-microservice-components";
import {useKeycloak} from "@react-keycloak/web";
import {getInitialPagination} from "@dreebit-registry/vsm-microservice-components";
import { paginationLimit } from "../../config/paginationLimit";
import { buildDynamicListFilter } from "../../utils/buildDynamicListFilter";
import { TypeDefinitionAttribute } from "../../types";
import { useNavigate } from "react-router-dom";
import {
  AddRounded,
  Dashboard2FilledRounded,
  DeviceHubRounded,
} from "@mui-symbols-material/w300";
import { TableColumnStorageConfig } from "../../types";
import { Chip, Typography } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import {connectionStatusChipFactory} from "../../utils/overallStatusIconFactory";
import { IOT_STATUS_COLOR_MAP } from "../../config/iotConnectionStatusConfig";
import moment from "moment";
import Table from "../Table";
import { useIndustryType } from "../../hooks/useIndustryType";
import { dropStaleBrandVisibility, filterColumnsForIndustry } from "../../utils/industryColumns";
import { useTableColumnConfig } from "../../hooks/useTableColumnConfig";
import { isSlaEnabled } from "../../utils/slaEnabled";
import { getSlaChipColors, getSlaLevelChipColors, getSlaLevelLabel, getSlaStatusLabel } from "../../config/iotSlaConfig";
import { formatValueWithUnit } from "../../utils/formatValueWithUnit";
import InstalledBaseNodeParentCell from "../InstalledBaseNodeParentCell";
import { isNodeInTrash } from "../../utils/installedBasePath";

// Coloured SLA chip (status, level, connectivity quality); empty cell without a value.
const slaChip = (label: string, colors: { bg: string; text: string }): React.ReactNode =>
  label ? (
    <Chip size="sm" variant="soft" sx={{ backgroundColor: colors.bg, color: colors.text, fontWeight: 600 }}>
      {label}
    </Chip>
  ) : null;

const conditionThresholdChip = (value: string | null | undefined, t: TFunction): React.ReactNode => {
  if (!value) return null;
  const colorMap: Record<string, 'danger' | 'warning' | 'success'> = {
    POOR: 'danger',
    MEDIUM: 'warning',
    GOOD: 'success',
  };
  const labelMap: Record<string, string> = {
    POOR: t('Poor'),
    MEDIUM: t('Medium'),
    GOOD: t('Good'),
  };
  const color = colorMap[value];
  if (!color) return null;
  return <Chip color={color}>{labelMap[value]}</Chip>;
};

// Network information arrives via the deferred SearchAssetsDeferred query. While
// it is in flight, rows with an IoT connection carry __networkInfoLoading and show
// a skeleton; rows without a connection stay empty right away.
const renderNetworkInfoCell =
  (getValue: (row: any) => React.ReactNode) =>
  (params: { row: any }): React.ReactNode => {
    if (params.row.__networkInfoLoading) {
      return <Skeleton variant="text" width={80} />;
    }
    return getValue(params.row) ?? null;
  };

export type ExtendedGridColDef<
    R extends GridValidRowModel = any,
    V = any,
    F = V
> = GridColDef<R, V, F> & {
  defaultHidden?: boolean;
};

interface Props {
  height: number;
  onChangePagination?: (newPaginationModel: GridPaginationModel) => void;
  /** Called with the `sort` URL value (`<column>:<asc|desc>`), or undefined when unsorted. */
  onChangeSort?: (sort?: string) => void;
  onDataLoaded?: (assetIds: string[]) => void;
  searchParams?: { query: string; fields: string[] };
  urlParams?: URLSearchParams;
  filter?: any;
  onOpenAsset?: (assetId: string) => void;
  columns?: ExtendedGridColDef[]; // custom columns deaktivieren Storage
  query?: DocumentNode;
  staticFilters?: Record<string, any>;
  attributes?: TypeDefinitionAttribute[];
  /**
   * Whether the filter TDAs behind `attributes` have settled. Injected by
   * `ListWithFilterDrawer`; omit it (or pass `true`) when `attributes` are available
   * synchronously. See the initial-fetch effect for why it matters.
   */
  attributesLoaded?: boolean;
  temporaryVisibleColumns?: string[];
  apiRef?: React.MutableRefObject<GridApiPro>;
  panelAnchorRef?: React.RefObject<HTMLElement | null>;
}

const getDefaultColumns = (
  t: TFunction,
  resources: string[],
  isIoTAdmin: boolean,
  onOpenAsset?: (assetId: string) => void,
  navigate?: ReturnType<typeof useNavigate>,
  locale?: string,
): ExtendedGridColDef[] => {
  return [
    {
      field: "title",
      headerName: t("Asset Name"),
      flex: 1,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.title") || _.get(row, "attributes.productName") || "";
      },
    },
    {
      field: "serialNumber",
      headerName: t("Serial No."),
      flex: 1,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.serialNumber");
      },
      renderCell: (params) => {
        const assetLocationId = _.get(params.row, "assetLocation.id");
        const serialNumber = _.get(params.row, "attributes.serialNumber");
        // Trashed assets stay listed until purged. The path behind the check comes
        // from the deferred query, so the badge follows once it has arrived.
        const inTrash = isNodeInTrash(_.get(params.row, "assetLocation.installedBaseNode"));

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0 }}>
            <Typography
              noWrap
              fontWeight={"bold"}
              sx={{ cursor: "pointer" }}
              color={"primary"}
              onClick={() => {
                if (onOpenAsset) onOpenAsset(assetLocationId);
              }}
            >
              {serialNumber}
            </Typography>
            {inTrash && (
              <Chip size="sm" color="warning" variant="soft">{t("In trash")}</Chip>
            )}
          </Box>
        );
      },
      display: "flex",
      hideable: false,
    },
    {
      field: "semiconductorTool",
      headerName: t("Semiconductor Tool"),
      flex: 1,
      valueGetter: (value, row) => _.get(row, "attributes.semiconductorToolTitle"),
    },
    {
      field: "semiconductorChamber",
      headerName: t("Semiconductor Chamber"),
      flex: 1,
      valueGetter: (value, row) => _.get(row, "attributes.semiconductorChamberTitle"),
    },
    {
      field: "imei",
      headerName: t("IMEI"),
      flex: 1,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.imei");
      },
    },
    {
      field: "product",
      headerName: t("Product Name"),
      flex: 1,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.productName");
      },
    },
    {
      field: "productCode",
      headerName: t("Product Code"),
      flex: 1,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.productCode");
      },
    },
    {
      field: "manufacturer",
      headerName: t("Manufacturer"),
      flex: 1,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.manufacturerName");
      },
    },
    {
      field: "client",
      headerName: t("Company"),
      flex: 1,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.clientName");
      },
    },
    {
      field: "country",
      headerName: t("Country"),
      flex: 1,
      defaultHidden: true,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.countryName");
      },
    },
    {
      field: "city",
      headerName: t("City"),
      flex: 1,
      defaultHidden: true,
      valueGetter: (value, row) => {
        return _.get(row, "attributes.city");
      },
    },
    {
      field: "location",
      headerName: t("Location"),
      flex: 1,
      defaultHidden: true,
      display: "flex",
      valueGetter: (value, row) => _.get(row, "attributes.parentNodeTitle") ?? "",
      // The index only knows the parent's title; icon and path tooltip follow once
      // the deferred ancestor path has arrived. The trash badge sits behind the
      // serial number — here it would mostly repeat the "Trash" parent title.
      renderCell: (params) =>
        _.get(params.row, "assetLocation.installedBaseNode.pathNodes") ? (
          <InstalledBaseNodeParentCell
            installedBaseNode={_.get(params.row, "assetLocation.installedBaseNode")}
            hideTrashBadge
          />
        ) : (
          <Typography noWrap>{params.value}</Typography>
        ),
    },
    {
      field: "iotConnection",
      headerName: t("IoT Asset Status"),
      minWidth: 200,
      display: "flex",
      align: "center",
      renderCell: (params) => {
        const connectionStatus = _.get(params.row, "attributes.iotConnectionStatus", undefined);

        return connectionStatusChipFactory(connectionStatus, t);
      },
    },
    {
      field: "firmwareVersion",
      headerName: t("Firmware Version"),
      flex: 1,
      defaultHidden: true,
      valueGetter: (value, row) => _.get(row, "attributes.firmwareVersion"),
    },
    {
      field: "modemFirmwareVersion",
      headerName: t("Modem Firmware Version"),
      flex: 1,
      defaultHidden: true,
      valueGetter: (value, row) => _.get(row, "attributes.modemFirmwareVersion"),
    },
    {
      field: "iotHubConnectivity",
      headerName: t("IoT Hub Connectivity"),
      minWidth: 180,
      display: "flex",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const iotHubConnectivityStatus = _.get(row, "attributes.iotHubConnectivityStatus");
        if (!iotHubConnectivityStatus) return null;
        return t(hubConnectivityFunc({ iotHubConnectivityStatus }));
      },
    },
    // SLA metrics from the search index — only selected with REACT_APP_ENABLE_SLA.
    ...(isSlaEnabled()
      ? ([
          {
            field: "slaStatus",
            headerName: t("SLA Status"),
            minWidth: 150,
            display: "flex",
            defaultHidden: true,
            renderCell: (params) => {
              const status = _.get(params.row, "attributes.iotSlaStatus");
              const level = _.get(params.row, "attributes.iotSlaLevel");
              return slaChip(getSlaStatusLabel(status, t), getSlaChipColors(status, level));
            },
          },
          {
            field: "slaLevel",
            headerName: t("SLA Level"),
            minWidth: 130,
            display: "flex",
            defaultHidden: true,
            renderCell: (params) => {
              const level = _.get(params.row, "attributes.iotSlaLevel");
              return slaChip(getSlaLevelLabel(level, t), getSlaLevelChipColors(level));
            },
          },
          {
            field: "connectivityQuality",
            headerName: t("Connectivity Quality"),
            minWidth: 170,
            display: "flex",
            defaultHidden: true,
            renderCell: (params) => {
              const quality = _.get(params.row, "attributes.iotConnectivityQuality");
              return slaChip(getSlaLevelLabel(quality, t), getSlaLevelChipColors(quality));
            },
          },
        ] as ExtendedGridColDef[])
      : []),
    {
      field: "simStatus",
      headerName: t("IoT Connection Status"),
      minWidth: 150,
      display: "flex",
      align: "center",
      defaultHidden: true,
      renderCell: renderNetworkInfoCell((row) => {
        const networkInformation = _.get(
          row,
          "assetLocation.iotConnection.networkInformation"
        );
        // Upstream gateway (gateway.gateway) — the asset's own IoT box is the
        // outer gateway; its imei is shown in the IMEI column instead.
        const upstreamGatewayImei = _.get(
          row,
          "assetLocation.iotConnection.gateway.gateway.imei"
        );
        if (!networkInformation && !upstreamGatewayImei) return null;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
            {upstreamGatewayImei && (
              <Tooltip
                arrow
                title={t("This asset has a gateway connection configured")}
              >
                <DeviceHubRounded
                  sx={{ color: IOT_STATUS_COLOR_MAP.RUNNING, fontSize: 24 }}
                />
              </Tooltip>
            )}
            {networkInformation && (
              <SignalStrengthIcon
                signalStrength={networkInformation.signalStrength}
                size={24}
              />
            )}
          </Box>
        );
      }),
    },
    {
      field: "simNetwork",
      headerName: t("SIM Network"),
      flex: 1,
      defaultHidden: true,
      renderCell: renderNetworkInfoCell((row) =>
        _.get(row, "assetLocation.iotConnection.networkInformation.currentStandard")
      ),
    },
    {
      field: "networkProvider",
      headerName: t("Network Provider"),
      flex: 1,
      defaultHidden: true,
      renderCell: renderNetworkInfoCell((row) =>
        _.get(row, "assetLocation.iotConnection.networkInformation.networkProvider")
      ),
    },
    {
      field: "harshCondition",
      headerName: t("Condition"),
      minWidth: 150,
      display: "flex",
      align: "center",
      defaultHidden: true,
      renderCell: (params) => conditionThresholdChip(_.get(params.row, "metrics.harshConditionThreshold"), t),
    },
    {
      field: "harshConditionScore",
      headerName: t("Harsh Condition Score"),
      minWidth: 180,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const score = _.get(row, "metrics.harshConditionScore");
        return score != null ? `${Math.round(score * 100)}%` : null;
      },
    },
    {
      field: "exhaustFilter",
      headerName: t("Exhaust Filter"),
      minWidth: 150,
      display: "flex",
      align: "center",
      defaultHidden: true,
      renderCell: (params) => conditionThresholdChip(_.get(params.row, "metrics.exhaustFilterConditionThreshold"), t),
    },
    {
      field: "exhaustFilterConditionScore",
      headerName: t("Exhaust Filter Score"),
      minWidth: 180,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const score = _.get(row, "metrics.exhaustFilterConditionScore");
        return score != null ? `${Math.round(score * 100)}%` : null;
      },
    },
    {
      field: "missingSensor",
      headerName: t("Missing Sensor Values"),
      minWidth: 160,
      display: "flex",
      align: "center",
      defaultHidden: true,
      renderCell: (params) => {
        const missing = _.get(params.row, "metrics.missingSensorValues");
        if (!missing) return null;
        return <Chip color="primary">{t("Missing sensor values")}</Chip>;
      },
    },
    {
      field: "lastMissingSensorValuesAt",
      headerName: t("Last Missing Sensor Values"),
      minWidth: 220,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const date = _.get(row, "metrics.lastMissingSensorValuesAt");
        return date ? moment(date).format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "sensorValueOutOfRange",
      headerName: t("Sensor Value out of Range"),
      minWidth: 200,
      display: "flex",
      align: "center",
      defaultHidden: true,
      renderCell: (params) => {
        const outOfRange = _.get(params.row, "metrics.sensorValueOutOfRange");
        if (!outOfRange) return null;
        return <Chip color="primary">{t("Out of range")}</Chip>;
      },
    },
    {
      field: "lastSensorValueOutOfRangeAt",
      headerName: t("Last Sensor Value out of Range"),
      minWidth: 240,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const date = _.get(row, "metrics.lastSensorValueOutOfRangeAt");
        return date ? moment(date).format('DD.MM.YYYY') : null;
      },
    },
    // Details of the reading that raised the out-of-range flag. The backend
    // nulls the whole object once a later event reports `false`, so all four
    // cells go empty together. `|| null` is safe: "0 hPa" is a truthy string.
    {
      field: "sensorValueOutOfRangeParameter",
      headerName: t("Out of Range Parameter"),
      minWidth: 200,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) =>
        _.get(row, "metrics.sensorValueOutOfRangeDetails.telemetryParameterLabel") ?? null,
    },
    {
      field: "sensorValueOutOfRangeRecordedValue",
      headerName: t("Out of Range Value"),
      minWidth: 180,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const details = _.get(row, "metrics.sensorValueOutOfRangeDetails");
        return formatValueWithUnit(details?.recordedSensorValue, details?.recordedSensorValueUnit, locale) || null;
      },
    },
    {
      field: "sensorValueOutOfRangeLowerThreshold",
      headerName: t("Lower Threshold"),
      minWidth: 170,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const details = _.get(row, "metrics.sensorValueOutOfRangeDetails");
        return formatValueWithUnit(details?.lowerSensorThresholdValue, details?.lowerSensorThresholdValueUnit, locale) || null;
      },
    },
    {
      field: "sensorValueOutOfRangeUpperThreshold",
      headerName: t("Upper Threshold"),
      minWidth: 170,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const details = _.get(row, "metrics.sensorValueOutOfRangeDetails");
        return formatValueWithUnit(details?.upperSensorThresholdValue, details?.upperSensorThresholdValueUnit, locale) || null;
      },
    },
    {
      field: "vsdCandidate",
      headerName: t("VSD Candidate"),
      minWidth: 150,
      display: "flex",
      align: "center",
      defaultHidden: true,
      renderCell: (params) => conditionThresholdChip(_.get(params.row, "metrics.vsdCandidateThreshold"), t),
    },
    {
      field: "vsdCandidateScore",
      headerName: t("VSD Score"),
      minWidth: 150,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const score = _.get(row, "metrics.vsdCandidateScore");
        return score != null ? `${Math.round(score * 100)}%` : null;
      },
    },
    {
      field: "maintenance",
      headerName: t("Maintenance"),
      minWidth: 160,
      display: "flex",
      align: "center",
      defaultHidden: true,
      renderCell: (params) => {
        const overdue = _.get(params.row, "metrics.maintenanceOverdue");
        const dueSoon = _.get(params.row, "metrics.maintenanceDueSoon");
        if (overdue) return <Chip color="danger">{t("Overdue")}</Chip>;
        if (dueSoon) return <Chip color="warning">{t("Due")}</Chip>;
        return null;
      },
    },
    {
      field: "lastMaintenanceDate",
      headerName: t("Last Maintenance"),
      minWidth: 200,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const date = _.get(row, "metrics.maintenanceSchedule.lastMaintenanceDate");
        return date ? moment(date).format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "lastExhaustFilterChangeDate",
      headerName: t("Last Exhaust Filter Change"),
      minWidth: 220,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const date = _.get(row, "metrics.maintenanceSchedule.lastExhaustFilterChangeDate");
        return date ? moment(date).format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "lastOilAndFilterChangeDate",
      headerName: t("Last Oil and Filter Change"),
      minWidth: 220,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const date = _.get(row, "metrics.maintenanceSchedule.lastOilAndFilterChangeDate");
        return date ? moment(date).format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "lastRemanufacturingDate",
      headerName: t("Last Remanufacturing"),
      minWidth: 200,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const date = _.get(row, "metrics.maintenanceSchedule.lastRemanufacturingDate");
        return date ? moment(date).format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "daysUntilNextExhaustFilterChange",
      headerName: t("Next Exhaust Filter Change"),
      minWidth: 220,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const days = _.get(row, "metrics.maintenanceSchedule.daysUntilNextExhaustFilterChange");
        return days != null ? moment().add(days, 'days').format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "daysUntilNextOilAndFilterChange",
      headerName: t("Next Oil and Filter Change"),
      minWidth: 220,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const days = _.get(row, "metrics.maintenanceSchedule.daysUntilNextOilAndFilterChange");
        return days != null ? moment().add(days, 'days').format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "daysUntilNextRemanufacturing",
      headerName: t("Next Remanufacturing"),
      minWidth: 200,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const days = _.get(row, "metrics.maintenanceSchedule.daysUntilNextRemanufacturing");
        return days != null ? moment().add(days, 'days').format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "daysUntilNextService",
      headerName: t("Next Service"),
      minWidth: 180,
      display: "flex",
      align: "center",
      defaultHidden: true,
      valueGetter: (value, row) => {
        const days = _.get(row, "metrics.maintenanceSchedule.daysUntilNextService");
        return days != null ? moment().add(days, 'days').format('DD.MM.YYYY') : null;
      },
    },
    {
      field: "actions",
      headerName: t("Actions"),
      width: 80,
      display: "flex",
      resizable: false,
      hideable: false,
      disableReorder: true,
      align: "center",
      renderCell: (params) => {
        const assetLocation = _.get(params.row, "assetLocation");
        const assetLocationId = _.get(assetLocation, "id");
        // Same frontend rule as the connect route guard (src/utils/iotAttachGate.ts).
        // The list query selects no industry type, so semiconductor is left to the
        // wizard / route guard; a blocked network scan is explained by the wizard too.
        // Installation and connection are known from the index right away; the
        // contract arrives with the deferred query, so the entry stays inactive until then.
        const canCreateIotDevice = isIotAttachEntryVisible({
          isIoTAdmin,
          isInstalled:
            !!_.get(params.row, "attributes.parentNodeId") ||
            !!_.get(assetLocation, "installedBaseNode.id"),
          hasIotConnection:
            !!_.get(params.row, "attributes.iotConnectionStatus") ||
            !!_.get(assetLocation, "iotConnection.id"),
          isPendingIotConnection: !!assetLocationId && !!getPendingIotConnection(assetLocationId),
          contractValid: isContractValid(_.get(assetLocation, 'owner.contract')),
          isSemiconductor: false,
        });

        return (
          <ActionsMenu
            items={[
              {
                item: (
                  <>
                    <Dashboard2FilledRounded
                      fontSize={"inherit"}
                      sx={{ mr: "4px" }}
                    />
                    {t("Dashboard")}
                  </>
                ),
                onClick: () => {
                  if (onOpenAsset) onOpenAsset(assetLocationId);
                },
                active: true,
              },
              {
                item: (
                  <>
                    <AddRounded fontSize={"inherit"} sx={{ mr: "4px" }} />
                    {t("Create IoT device")}
                  </>
                ),
                onClick: () => {
                  if (navigate) navigate(`/assets/${assetLocationId}/connect`);
                },
                active: canCreateIotDevice,
              },
            ]}
          />
        );
      },
    },
  ];
};

// The row actions stay pinned right and are not offered in the columns panel
const FIXED_COLUMN_FIELDS = ["actions"];

// Columns with an explicit fixed width (e.g. actions) keep that width; all others use
// flex + minWidth so they grow when space is available but trigger horizontal scroll
// instead of shrinking — until the user resized them.
const applyColumnWidths = (
  columns: ExtendedGridColDef[],
  columnWidths: Record<string, number>
): ExtendedGridColDef[] =>
  columns.map((col) => {
    const savedWidth = columnWidths[col.field];

    if (col.width != null) {
      return { ...col, width: savedWidth ?? col.width };
    }

    return {
      ...col,
      width: savedWidth,
      flex: savedWidth ? undefined : 1,
      minWidth: col.minWidth ?? 150,
    };
  });

const AssetsTable = ({
  height,
  onChangePagination,
  onChangeSort,
  onDataLoaded,
  searchParams,
  urlParams,
  filter,
  onOpenAsset,
  columns,
  query = SearchAssets,
  staticFilters,
  attributes,
  attributesLoaded,
  temporaryVisibleColumns,
  apiRef,
  panelAnchorRef,
}: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const resources = useUserResources();
  const { keycloak } = useKeycloak();
  const industryType = useIndustryType();

  const isIoTAdmin = useMemo(() => isIoTAdminToken(keycloak.token), [keycloak.token]);

  const usingCustomColumns = !!columns;
  // Stable identity is load-bearing: the column-config effect below depends on
  // defaultColumns and writes state — an unmemoized array would re-run it every render.
  const defaultColumns = useMemo(() => {
    if (columns) return columns;
    return filterColumnsForIndustry(
      getDefaultColumns(t, resources, isIoTAdmin, onOpenAsset, navigate, i18n.language),
      industryType
    ).map((col) => ({ ...col, sortable: isAssetListColumnSortable(col.field) }));
  }, [columns, t, resources, isIoTAdmin, onOpenAsset, navigate, industryType, i18n.language]);

  const [paginationModel, setPaginationModel] = useState(
    getInitialPagination(paginationLimit, urlParams)
  );

  // Server-side sorting (IBM-1053), one criterion; custom-column lists (import) stay unsorted.
  const [sortModel, setSortModel] = useState<GridSortModel>(() =>
    usingCustomColumns ? [] : parseAssetListSortParam(urlParams?.get("sort"))
  );
  const handleSortModelChange = (model: GridSortModel) => {
    const next = model.slice(0, 1);
    setSortModel(next);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    if (onChangeSort) onChangeSort(formatAssetListSortParam(next));
  };

  // Default visibility from the columns' `defaultHidden`. Filter-driven (temporary)
  // columns are shown on top, count as default for Reset, but are never persisted.
  const defaultVisibility = useMemo(
    () => Object.fromEntries(defaultColumns.map((col) => [col.field, !col.defaultHidden])),
    [defaultColumns]
  );
  const migrateColumnConfig = useCallback(
    (saved: TableColumnStorageConfig) => dropStaleBrandVisibility(saved, industryType),
    [industryType]
  );
  const columnConfig = useTableColumnConfig({
    storageKey: 'assetList_columnConfig',
    columns: defaultColumns,
    defaultVisibility,
    forceVisible: temporaryVisibleColumns,
    fixedFields: FIXED_COLUMN_FIELDS,
    persistWidths: true,
    enabled: !usingCustomColumns, // keine Speicherung bei Custom Columns
    migrate: migrateColumnConfig,
    stamp: { industryType },
  });
  const { columnVisibilityModel } = columnConfig;
  const tableColumns = useMemo(
    () => applyColumnWidths(columnConfig.columns, columnConfig.columnWidths),
    [columnConfig.columns, columnConfig.columnWidths]
  );

  // Keyed on the search *value*, not on the object identity: `searchParams` is recreated
  // on every URL change, so an identity dependency reset the page (and wrote the URL again)
  // on unrelated navigations.
  const searchParamsKey = `${searchParams?.query ?? ""}|${(searchParams?.fields ?? []).join(",")}`;
  useEffect(() => {
    setPaginationModel({
      page: getInitialPagination(paginationLimit, urlParams).page,
      pageSize: getInitialPagination(paginationLimit, urlParams).pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsKey]);

  useEffect(() => {
    if (onChangePagination) {
      onChangePagination(paginationModel);
    }
  }, [paginationModel]);

  const listVariables = {
    withSla: isSlaEnabled(),
    // Without a search term the selected `fields` cannot change the result, so the whole
    // search object is omitted ($query is nullable). Sending it anyway made the search-TDA
    // reconciliation — which fills the initially empty `fields` with the backend defaults —
    // change the variables and trigger a second, identical network-only request.
    query: searchParams?.query
      ? {
          query: searchParams.query,
          fields: searchParams.fields,
        }
      : undefined,
    pagination: {
      start: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
    },
    sort: toAssetListSort(sortModel),
    filter: buildDynamicListFilter(
      filter,
      attributes?.filter(a => a.index !== 'search') || [],
      {
        ...(staticFilters || {}),
        ...(filter?.geolocation
          ? { geolocation: typeof filter.geolocation === 'string' ? JSON.parse(filter.geolocation) : filter.geolocation }
          : {}),
      }
    ),
  };

  const [fetchAssetList, assetListQuery] = useLazyQuery(query, {
    variables: listVariables,
    context: {
      clientName: "federatedGraphEndpoint",
    },
    fetchPolicy: "network-only",
  });
  const assetsListData = _.get(
    assetListQuery,
    "data.searchAssets.searchAssetResults",
    []
  );
  const totalAssets = _.get(assetListQuery, "data.searchAssets.total");

  // URL filter values only become GraphQL filter clauses once the filter TDAs are loaded
  // (`buildDynamicListFilter` iterates over the attributes, not over the values). Firing
  // before that renders an unfiltered page and forces an immediate second request, so the
  // first fetch waits — but only when there is a filter at all, leaving header navigation
  // (no URL params) as fast as before. The latch keeps a later `attributesLoaded` flip
  // (the drawer refetches its TDAs) from issuing an extra network-only request; from here
  // on Apollo's own reobserve drives the query.
  const needsAttributes = Object.keys(filter ?? {}).length > 0;
  const listFetchStartedRef = useRef(false);
  useEffect(() => {
    if (listFetchStartedRef.current) return;
    if (needsAttributes && attributesLoaded === false) return;
    listFetchStartedRef.current = true;
    fetchAssetList();
  }, [fetchAssetList, needsAttributes, attributesLoaded]);

  // Deferred fields: what the search index does not carry (contract for the
  // actions column, the ancestor path, network information) is fetched by a
  // second query for the same page and merged into the rows, so the table itself
  // renders straight from the index. Path and network information only count
  // for visible columns (the model holds an explicit value per column).
  const deferredFlags = useMemo(() => {
    if (usingCustomColumns || !columnVisibilityModel) {
      return { withActions: false, withPath: false, withNetworkInfo: false };
    }
    const visibleFields = Object.keys(columnVisibilityModel).filter(
      (field) => columnVisibilityModel[field] === true
    );
    // The path is always needed: the serial number column (never hideable) shows
    // the trash badge, which the index alone cannot tell.
    return { ...getDeferredFieldFlags(visibleFields, true), withPath: true };
  }, [columnVisibilityModel, usingCustomColumns]);
  const anyDeferredFlag = hasAnyDeferredFlag(deferredFlags);

  const deferredQuery = useQuery(SearchAssetsDeferred, {
    variables: { ...listVariables, ...deferredFlags },
    // Same page as the main query: wait for it so both requests can't race a
    // filter change into mismatching pages.
    skip: !anyDeferredFlag || !assetListQuery.called,
    context: {
      clientName: "federatedGraphEndpoint",
    },
    // no-cache: the backend nulls a whole iotConnection when its network lookup
    // fails, which must not clobber cached connections; the results are merged
    // into the rows manually anyway, so the cache adds nothing here.
    fetchPolicy: "no-cache",
    // Partial resolver failures (single rows' lookups) must not blank the whole
    // page's deferred cells — keep whatever data arrived.
    errorPolicy: "all",
  });

  const deferredByLocationId = useMemo(() => {
    const map = new Map<string, any>();
    const results = _.get(deferredQuery.data, "searchAssets.searchAssetResults", []);
    results.forEach((result: any) => {
      const assetLocation = _.get(result, "assetLocation");
      if (assetLocation?.id) map.set(assetLocation.id, assetLocation);
    });
    return map;
  }, [deferredQuery.data]);

  const rows = useMemo(() => {
    if (!anyDeferredFlag) return assetsListData;
    return assetsListData.map((row: any) => {
      const deferred = deferredByLocationId.get(_.get(row, "assetLocation.id"));
      if (!deferred) {
        const networkInfoPending =
          deferredQuery.loading &&
          deferredFlags.withNetworkInfo &&
          !!_.get(row, "attributes.iotConnectionStatus");
        return networkInfoPending ? { ...row, __networkInfoLoading: true } : row;
      }
      return {
        ...row,
        assetLocation: { ...row.assetLocation, ...deferred },
      };
    });
  }, [assetsListData, deferredByLocationId, anyDeferredFlag, deferredFlags.withNetworkInfo, deferredQuery.loading]);

  useEffect(() => {
    const ids = assetsListData.map((r: any) => _.get(r, "assetLocation.id"));
    if (onDataLoaded) onDataLoaded(ids);
  }, [assetsListData]);

  return (
    <div style={{ width: "100%", height: height }}>
      <Table
        // `called` covers the window in which the first fetch is still gated on the
        // filter TDAs — without it the grid would briefly claim it has no rows.
        loading={!assetListQuery.called || assetListQuery.loading}
        data={rows}
        getRowId={(res: any) => _.get(res, "assetLocation.id")}
        rowCount={totalAssets}
        {...columnConfig.gridProps}
        columns={tableColumns}
        pagination={true}
        paginationMode="server"
        paginationModel={paginationModel}
        pageSizeOptions={[paginationLimit]}
        onPaginationModelChange={setPaginationModel}
        apiRef={apiRef}
        panelAnchorRef={panelAnchorRef}
        pinnedColumns={{ right: ['actions'] }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        disableColumnSorting={usingCustomColumns}
        // Custom columns (import list) are not persisted, so a moved column would snap back
        disableColumnReorder={usingCustomColumns}
        disableMultipleColumnsSorting
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
      />
    </div>
  );
};

export default AssetsTable;
